import { onSnapshot } from "firebase/firestore"
import {
  customerDoc,
  jobDoc,
  jobsCollection,
  painsCollection,
  participantsCollection,
  picksCollection,
  sessionDoc,
  toCardPick,
  toCustomer,
  toJob,
  toPain,
  toParticipant,
  toSession,
} from "@/lib/firebase/firestore"
import { byCreatedAt, byJoinedAt, pendingAsNow } from "@/utils/timestamps"
import type { CardPick, Customer, Job, Pain, Participant, Session } from "@/types"

/** Called with null when the document does not exist. */
type Listener<T> = (value: T) => void
type ErrorListener = (error: Error) => void
export type Unsubscribe = () => void

export function subscribeToCustomer(
  customerId: string,
  onChange: Listener<Customer | null>,
  onError?: ErrorListener
): Unsubscribe {
  return onSnapshot(
    customerDoc(customerId),
    (snapshot) => onChange(toCustomer(snapshot)),
    (error) => onError?.(error)
  )
}

/**
 * Session state, round/phase, config, timer, lifecycle and the current job
 * pointer all live on this one document, so a single listener keeps every one of
 * them live.
 */
export function subscribeToSession(
  sessionId: string,
  onChange: Listener<Session | null>,
  onError?: ErrorListener
): Unsubscribe {
  return onSnapshot(
    sessionDoc(sessionId),
    (snapshot) => onChange(toSession(snapshot)),
    (error) => onError?.(error)
  )
}

/** Covers roster changes plus each participant's ready state and finished gate. */
export function subscribeToParticipants(
  sessionId: string,
  onChange: Listener<Participant[]>,
  onError?: ErrorListener
): Unsubscribe {
  return onSnapshot(
    participantsCollection(sessionId),
    (snapshot) => {
      const participants = snapshot.docs
        .map(toParticipant)
        .filter((p): p is Participant => p !== null)
        .sort(byJoinedAt)
      onChange(participants)
    },
    (error) => onError?.(error)
  )
}

/**
 * The board of pain points, and the votes on them.
 *
 * One listener for both: a vote is an array on the pain it was cast for, so the
 * tally arrives with the same snapshot as the text. A separate votes collection
 * would need a second listener and could deliver a count for a pain the client
 * had not yet seen.
 *
 * Opened for the whole session, not per round: the facilitator watches pains
 * appear as the room writes them, and the job screen still names the pains it
 * came from long after the Pains round is over.
 */
export function subscribeToPains(
  sessionId: string,
  onChange: Listener<Pain[]>,
  onError?: ErrorListener
): Unsubscribe {
  return onSnapshot(
    painsCollection(sessionId),
    (snapshot) => {
      const pains = snapshot.docs
        .map(toPain)
        .filter((p): p is Pain => p !== null)
        .sort(byCreatedAt)
      onChange(pains)
    },
    (error) => onError?.(error)
  )
}

/**
 * The job currently being worked. Subscribed as a CHILD of the session listener:
 * which job is current arrives on the session document, so this can only be
 * opened once that is known, and must be re-pointed when it changes.
 *
 * The statement, the shortlist, every placement and the whole deep dive are
 * fields of this one document, which is what lets the room watch the facilitator
 * file cards in real time without a listener per card.
 */
export function subscribeToJob(
  sessionId: string,
  jobId: string,
  onChange: Listener<Job | null>,
  onError?: ErrorListener
): Unsubscribe {
  return onSnapshot(
    jobDoc(sessionId, jobId),
    (snapshot) => onChange(toJob(snapshot)),
    (error) => onError?.(error)
  )
}

/**
 * Every job in the session, for the gate between jobs and for the report.
 *
 * Separate from the current-job listener even though it also delivers the
 * current one: the report needs jobs the session is no longer pointed at, and
 * merging the two would mean re-deriving "which is current" on every snapshot.
 */
export function subscribeToJobs(
  sessionId: string,
  onChange: Listener<Job[]>,
  onError?: ErrorListener
): Unsubscribe {
  return onSnapshot(
    jobsCollection(sessionId),
    (snapshot) => {
      const jobs = snapshot.docs
        .map(toJob)
        .filter((j): j is Job => j !== null)
        .sort(byCreatedAt)
      onChange(jobs)
    },
    (error) => onError?.(error)
  )
}

/**
 * What each person picked for the current job — one document per participant, so
 * the facilitator's tally updates as the room selects.
 *
 * A child of the current-job subscription for the same reason the job itself is.
 */
export function subscribeToPicks(
  sessionId: string,
  jobId: string,
  onChange: Listener<CardPick[]>,
  onError?: ErrorListener
): Unsubscribe {
  return onSnapshot(
    picksCollection(sessionId, jobId),
    (snapshot) => {
      const picks = snapshot.docs
        .map(toCardPick)
        .filter((p): p is CardPick => p !== null)
        .sort(
          (a, b) =>
            pendingAsNow(a.updatedAt) - pendingAsNow(b.updatedAt) || a.id.localeCompare(b.id)
        )
      onChange(picks)
    },
    (error) => onError?.(error)
  )
}
